import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { getHomePathForRole } from "./modules/registry";
import { AuthProvider } from "./context/AuthContext";
import { NateMProvider, useNateM } from "./context/NateMContext";
import { ScriptPanelProvider } from "./context/ScriptPanelContext";
import { EditConfirmProvider } from "./context/EditConfirmContext";
import { LeadFocusProvider } from "./context/LeadFocusContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "./components/ui/sonner";
import { NateMWidget } from "./components/shared/NateMWidget";
import { HorizontalScrollStrip } from "./components/shared/HorizontalScrollStrip";
import { ViewAsBanner } from "./components/shared/ViewAsBanner";
import { MaintenanceStatusPill } from "./components/layout/MaintenanceStatusPill";
import JoinPage from "./pages/JoinPage";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ClientDashboardPage from "./pages/ClientDashboardPage";
import ReportArchivePage from "./pages/ReportArchivePage";
import ClientTrustPage from "./pages/ClientTrustPage";
import ClientFundingPage from "./pages/ClientFundingPage";
import ClientAffiliatePage from "./pages/ClientAffiliatePage";
import AdminHomePage from "./pages/AdminHomePage";
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AdminTrustPage from "./pages/AdminTrustPage";
import AdminFundingPage from "./pages/AdminFundingPage";
import AdminAffiliatePage from "./pages/AdminAffiliatePage";
import AdminManagementTeamPage from "./pages/AdminManagementTeamPage";
import AdminLeadsPage from "./pages/AdminLeadsPage";
import AdminNotificationsPage from "./pages/AdminNotificationsPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import SecurityCenterPage from "./pages/SecurityCenterPage";
import SetPasswordPage from "./pages/SetPasswordPage";
import ClientProfileSetupPage from "./pages/ClientProfileSetupPage";
import ClientProfilePage from "./pages/ClientProfilePage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminLeadPackagesPage from "./pages/AdminLeadPackagesPage";
import AdminRepActivityPage from "./pages/AdminRepActivityPage";
import AdminLeadStagingPage from "./pages/AdminLeadStagingPage";
import AdminRejectedLeadsPage from "./pages/AdminRejectedLeadsPage";
import AdminNiaPromptPage from "./pages/AdminNiaPromptPage";
import AdminLeadQualityPage from "./pages/AdminLeadQualityPage";
import AdminDiscardLogPage from "./pages/AdminDiscardLogPage";
import AdminVerificationAuditPage from "./pages/AdminVerificationAuditPage";
import AdminReplenishmentSettingsPage from "./pages/AdminReplenishmentSettingsPage";
import AdminRelayHistoryPage from "./pages/AdminRelayHistoryPage";
import AdminCommandCenterPage from "./pages/AdminCommandCenterPage";
import BackOfficePage from "./pages/BackOfficePage";
import BackofficeTrustIntakePage from "./pages/BackofficeTrustIntakePage";
import AdminFundingPipelinePage from "./pages/AdminFundingPipelinePage";
import ClientFundingPipelinePage from "./pages/ClientFundingPipelinePage";
import ManagerDashboardPage from "./pages/ManagerDashboardPage";
import RepDashboardPage from "./pages/RepDashboardPage";
import RepDetailDrilldownPage from "./pages/RepDetailDrilldownPage";
import AdminTrustControlPanelPage from "./pages/AdminTrustControlPanelPage";
import TrustPortalLoginPage from "./pages/TrustPortalLoginPage";
import TrustPortalSignupPage from "./pages/TrustPortalSignupPage";
import PublicReferralPage from "./pages/PublicReferralPage";
import TrustPortalDashboardPage from "./pages/TrustPortalDashboardPage";
import TrustPortalIntakePage from "./pages/TrustPortalIntakePage";
import TrustPortalComingSoonPage from "./pages/TrustPortalComingSoonPage";
import AdminFinancialTrackerPage from "./pages/AdminFinancialTrackerPage";
import AdminRepScorecardsPage from "./pages/AdminRepScorecardsPage";
import AdminComplianceInboxPage from "./pages/AdminComplianceInboxPage";
import AdminActiveSessionsPage from "./pages/AdminActiveSessionsPage";
import AdminLoginReportPage from "./pages/AdminLoginReportPage";
import AdminSignedAgreementsPage from "./pages/AdminSignedAgreementsPage";
import AdminDealsSummaryPage from "./pages/AdminDealsSummaryPage";
import AdminLeadBatchesPage from "./pages/AdminLeadBatchesPage";
import AdminWebsiteVisitorsPage from "./pages/AdminWebsiteVisitorsPage";
import LeadPullConfirmPage from "./pages/LeadPullConfirmPage";

function RootRedirect() {
  if (typeof window !== "undefined" && window.location.hostname.startsWith("trust.")) {
    return <Navigate to="/trust-portal/login" replace />;
  }
  return <Navigate to="/login" replace />;
}

function NotFoundRedirect() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  return <Navigate to={getHomePathForRole(user.role)} replace />;
}

function AppToaster() {
  const { user } = useAuth();
  const { minimized } = useNateM();
  const offset = user ? (minimized ? 56 : 360) + 16 : 16;
  return <Toaster theme="dark" position="top-right" style={{ right: offset }} />;
}

function App() {
  return (
    <AuthProvider>
      <LeadFocusProvider>
      <NateMProvider>
        <ScriptPanelProvider>
        <EditConfirmProvider>
      <BrowserRouter>
        <AppToaster />
        <NateMWidget />
        <HorizontalScrollStrip />
        <ViewAsBanner />
        <MaintenanceStatusPill />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/confirm-pull" element={<LeadPullConfirmPage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/client" element={<ProtectedRoute roles={["client"]}><ClientDashboardPage /></ProtectedRoute>} />
          <Route path="/client/archive" element={<ProtectedRoute roles={["client"]}><ReportArchivePage /></ProtectedRoute>} />
          <Route path="/client/trust" element={<ProtectedRoute roles={["client"]} requireModule="trust"><ClientTrustPage /></ProtectedRoute>} />
          <Route path="/client/funding" element={<ProtectedRoute roles={["client"]} requireModule="funding"><ClientFundingPage /></ProtectedRoute>} />
          <Route path="/client/affiliate" element={<ProtectedRoute roles={["client", "affiliate"]} requireModule="affiliate"><ClientAffiliatePage /></ProtectedRoute>} />
          <Route path="/client/funding-pipeline" element={<ProtectedRoute roles={["client"]} requireModule="funding_pipeline"><ClientFundingPipelinePage /></ProtectedRoute>} />
          <Route path="/client/profile" element={<ProtectedRoute roles={["client", "affiliate"]}><ClientProfilePage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminHomePage /></ProtectedRoute>} />
          <Route path="/admin/ecommerce" element={<ProtectedRoute roles={["admin"]}><AdminDashboardPage /></ProtectedRoute>} />
          <Route path="/admin/trust" element={<ProtectedRoute roles={["admin"]}><AdminTrustPage /></ProtectedRoute>} />
          <Route path="/admin/funding" element={<ProtectedRoute roles={["admin"]}><AdminFundingPage /></ProtectedRoute>} />
          <Route path="/admin/affiliate" element={<ProtectedRoute roles={["admin"]}><AdminAffiliatePage /></ProtectedRoute>} />
          <Route path="/admin/management-team" element={<ProtectedRoute roles={["admin"]}><AdminManagementTeamPage /></ProtectedRoute>} />
          <Route path="/admin/leads" element={<ProtectedRoute roles={["admin"]}><AdminLeadsPage /></ProtectedRoute>} />
          <Route path="/admin/notifications" element={<ProtectedRoute roles={["admin"]}><AdminNotificationsPage /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute roles={["admin"]}><AdminSettingsPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute roles={["admin"]}><AdminUsersPage /></ProtectedRoute>} />
          <Route path="/admin/lead-packages" element={<ProtectedRoute roles={["admin"]}><AdminLeadPackagesPage /></ProtectedRoute>} />
          <Route path="/admin/rep-activity" element={<ProtectedRoute roles={["admin"]}><AdminRepActivityPage /></ProtectedRoute>} />
          <Route path="/admin/lead-staging" element={<ProtectedRoute roles={["admin"]}><AdminLeadStagingPage /></ProtectedRoute>} />
          <Route path="/admin/rejected-leads" element={<ProtectedRoute roles={["admin"]}><AdminRejectedLeadsPage /></ProtectedRoute>} />
          <Route path="/admin/nia-prompt" element={<ProtectedRoute roles={["admin"]}><AdminNiaPromptPage /></ProtectedRoute>} />
          <Route path="/admin/lead-quality" element={<ProtectedRoute roles={["admin"]}><AdminLeadQualityPage /></ProtectedRoute>} />
          <Route path="/admin/discards" element={<ProtectedRoute roles={["admin"]}><AdminDiscardLogPage /></ProtectedRoute>} />
          <Route path="/admin/verification-audit" element={<ProtectedRoute roles={["admin"]}><AdminVerificationAuditPage /></ProtectedRoute>} />
          <Route path="/admin/replenishment-settings" element={<ProtectedRoute roles={["admin"]}><AdminReplenishmentSettingsPage /></ProtectedRoute>} />
          <Route path="/admin/relay-history" element={<ProtectedRoute roles={["admin"]}><AdminRelayHistoryPage /></ProtectedRoute>} />
          <Route path="/admin/command-center" element={<ProtectedRoute roles={["admin"]}><AdminCommandCenterPage /></ProtectedRoute>} />
          <Route path="/admin/rep-activity/:repId" element={<ProtectedRoute roles={["admin"]}><RepDetailDrilldownPage /></ProtectedRoute>} />
          <Route path="/manager/reps/:repId" element={<ProtectedRoute roles={["manager"]}><RepDetailDrilldownPage /></ProtectedRoute>} />
          <Route path="/admin/funding-pipeline" element={<ProtectedRoute roles={["admin"]}><AdminFundingPipelinePage /></ProtectedRoute>} />
          <Route path="/manager" element={<ProtectedRoute roles={["manager"]}><ManagerDashboardPage /></ProtectedRoute>} />
          <Route path="/rep" element={<ProtectedRoute roles={["affiliate"]}><RepDashboardPage /></ProtectedRoute>} />
          <Route path="/admin/trust-control-panel" element={<ProtectedRoute roles={["admin"]}><AdminTrustControlPanelPage /></ProtectedRoute>} />
          <Route path="/admin/financial-tracker" element={<ProtectedRoute roles={["admin"]}><AdminFinancialTrackerPage /></ProtectedRoute>} />
          <Route path="/admin/rep-scorecards" element={<ProtectedRoute roles={["admin"]}><AdminRepScorecardsPage /></ProtectedRoute>} />
          <Route path="/admin/compliance-inbox" element={<ProtectedRoute roles={["admin"]}><AdminComplianceInboxPage /></ProtectedRoute>} />
          <Route path="/admin/active-sessions" element={<ProtectedRoute roles={["admin"]}><AdminActiveSessionsPage /></ProtectedRoute>} />
          <Route path="/admin/login-report" element={<ProtectedRoute roles={["admin", "manager"]}><AdminLoginReportPage /></ProtectedRoute>} />
          <Route path="/admin/signed-agreements" element={<ProtectedRoute roles={["admin"]}><AdminSignedAgreementsPage /></ProtectedRoute>} />
          <Route path="/admin/deals-summary" element={<ProtectedRoute roles={["admin"]}><AdminDealsSummaryPage /></ProtectedRoute>} />
          <Route path="/admin/lead-batches" element={<ProtectedRoute roles={["admin"]}><AdminLeadBatchesPage /></ProtectedRoute>} />
          <Route path="/admin/batches" element={<ProtectedRoute roles={["admin"]}><AdminLeadBatchesPage /></ProtectedRoute>} />
          <Route path="/admin/website-visitors" element={<ProtectedRoute roles={["admin"]}><AdminWebsiteVisitorsPage /></ProtectedRoute>} />
          <Route path="/trust-portal/login" element={<TrustPortalLoginPage />} />
          <Route path="/trust-portal/signup" element={<TrustPortalSignupPage />} />
          <Route path="/ref/:code" element={<PublicReferralPage />} />
          <Route path="/trust-portal/coming-soon" element={<TrustPortalComingSoonPage />} />
          <Route path="/trust-portal/dashboard" element={<ProtectedRoute roles={["trust_client"]}><TrustPortalDashboardPage /></ProtectedRoute>} />
          <Route path="/trust-portal/intake" element={<ProtectedRoute roles={["trust_client"]}><TrustPortalIntakePage /></ProtectedRoute>} />
          <Route path="/security" element={<ProtectedRoute><SecurityCenterPage /></ProtectedRoute>} />
          <Route path="/set-password" element={<ProtectedRoute><SetPasswordPage /></ProtectedRoute>} />
          <Route path="/client/complete-profile" element={<ProtectedRoute roles={["client", "affiliate"]}><ClientProfileSetupPage /></ProtectedRoute>} />
          <Route path="/backoffice" element={<ProtectedRoute roles={["backoffice"]}><BackOfficePage /></ProtectedRoute>} />
          <Route path="/backoffice/trust-intake" element={<ProtectedRoute roles={["backoffice"]}><BackofficeTrustIntakePage /></ProtectedRoute>} />
          <Route path="/" element={<RootRedirect />} />
          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
      </BrowserRouter>
        </EditConfirmProvider>
        </ScriptPanelProvider>
      </NateMProvider>
      </LeadFocusProvider>
    </AuthProvider>
  );
}

export default App;
